// @ts-check
/**
 * ticketsRules.js - the Tickets page's pure rules: labels, age, the page a
 * ticket was sent from, and the "Copy for Claude" block. No React, no fetch.
 */

export const STATUSES = ['open', 'in_progress', 'done', 'wont_do'];
export const STATUS_LABELS = { open: 'Open', in_progress: 'In progress', done: 'Done', wont_do: "Won't do" };
export const PRIORITIES = ['low', 'normal', 'high'];
export const PRIORITY_LABELS = { low: 'Low', normal: 'Normal', high: 'High' };
export const KIND_LABELS = { bug: 'Bug', idea: 'Idea' };
// the page opens on what is still to do
export const DEFAULT_STATUSES = ['open', 'in_progress'];

/**
 * '3d', '5h', 'now' since `when`.
 * @param {string|Date|null|undefined} when
 * @param {Date} [now]
 */
export function ageOf(when, now = new Date()) {
  if (!when) return '';
  const mins = Math.max(0, Math.floor((now.getTime() - new Date(when).getTime()) / 60000));
  if (mins < 60) return mins < 1 ? 'now' : `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/**
 * The page a ticket names: "Projects - https://ops.globalat.com/#/projects"
 * -> { label: 'Projects', url: 'https://...' }. Only an http(s) link is a link.
 * @param {string|null|undefined} page
 * @returns {{ label: string, url: string|null }}
 */
export function pageOf(page) {
  const s = String(page || '').trim();
  const m = /(https?:\/\/\S+)\s*$/.exec(s);
  if (!m) return { label: s, url: null };
  const label = s
    .slice(0, m.index)
    .replace(/\s*-\s*$/, '')
    .trim();
  return { label: label || m[1], url: m[1] };
}

/**
 * A ticket as markdown to paste to Claude: everything needed to start on it.
 * `origin` makes the file links absolute (they need a signed-in session).
 * @param {any} t the ticket from GET /api/tickets/:id
 * @param {string} [origin]
 */
export function copyForClaude(t, origin = '') {
  const page = pageOf(t.page);
  const lines = [
    `## GlobalOps ticket ${t.number} - ${KIND_LABELS[t.kind] || t.kind}: ${t.subject || '(no subject)'}`,
    '',
    `- From: ${t.from_name ? `${t.from_name} <${t.from_email}>` : t.from_email || 'unknown'}`,
    `- Sent: ${t.created_at ? new Date(t.created_at).toISOString().replace('T', ' ').slice(0, 16) + ' UTC' : 'unknown'}`,
    `- Page: ${page.url ? `${page.label} (${page.url})` : page.label || 'not given'}`,
    `- Status: ${STATUS_LABELS[t.status] || t.status}, priority ${PRIORITY_LABELS[t.priority] || t.priority}`,
  ];
  if (t.user_agent) lines.push(`- Browser: ${t.user_agent}`);
  if (t.source === 'email_import' && t.source_note) lines.push(`- ${t.source_note}`);
  lines.push('', '### Details', '', String(t.details || '(no details)').trim(), '');
  const files = t.files || [];
  if (files.length) {
    lines.push('### Files (signed-in GlobalOps link)', '');
    for (const f of files) lines.push(`- ${f.filename}: ${origin}${f.url}`);
    lines.push('');
  }
  if (t.resolution_note) lines.push('### Resolution note', '', t.resolution_note, '');
  lines.push(`Read it with: node server/scripts/tickets.js show ${t.number}`);
  return lines.join('\n');
}

/**
 * The list after the search box (the server filters too; this keeps the
 * table right while a re-read is on its way).
 * @param {any[]} rows
 * @param {string} q
 */
export function searchRows(rows, q) {
  const s = String(q || '')
    .trim()
    .toLowerCase();
  if (!s) return rows;
  return rows.filter((t) =>
    [t.number, t.subject, t.from_email, t.from_name, t.page].some((v) =>
      String(v || '')
        .toLowerCase()
        .includes(s)
    )
  );
}
